import React, { useState, useContext, useMemo, useRef, useEffect } from 'react';
import { TicketContext } from '../../../../app/providers/TicketContext';
import { PanelContext } from '../../../../app/providers/PanelContext';

const MAX_DESC_LENGTH = 48;

function NewTicket() {
  const [description, setDescription] = useState('');
  const [selectedFlow, setSelectedFlow] = useState('');
  const [activeCategory, setActiveCategory] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const searchRef = useRef(null);
  const { createTicket, setTickets, flows, selectTicket, setSelectedTicketId } = useContext(TicketContext);
  const { setSelectedPanel, requestDetailView } = useContext(PanelContext);

  const remainingChars = MAX_DESC_LENGTH - description.length;

  const categorizedFlows = useMemo(() => {
    const groups = (flows || []).reduce((acc, flow) => {
      const category = (flow.FCATEGORY || 'Diğer').trim() || 'Diğer';
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(flow);
      return acc;
    }, {});

    return Object.keys(groups)
      .sort((a, b) => a.localeCompare(b, 'tr'))
      .map((category) => ({
        category,
        items: groups[category]
          .slice()
          .sort((a, b) => (a.MAINFLOW || '').localeCompare(b.MAINFLOW || '', 'tr')),
      }));
  }, [flows]);

  const activeGroup = useMemo(
    () => categorizedFlows.find((group) => group.category === activeCategory) || null,
    [categorizedFlows, activeCategory],
  );

  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return null;
    const q = searchQuery.toLowerCase();
    return (flows || []).filter(
      (f) =>
        (f.MAINFLOW || '').toLowerCase().includes(q) ||
        (f.PROCESS || '').toLowerCase().includes(q),
    );
  }, [flows, searchQuery]);

  const handleDescriptionChange = (event) => {
    const value = event.target.value.toUpperCase().slice(0, MAX_DESC_LENGTH);
    setDescription(value);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!selectedFlow) {
      return;
    }

    const newTicketPayload = {
      token: localStorage.getItem('token'),
      TICID: null,
      CUSID: '1',
      MEMID: null,
      FLOWID: selectedFlow,
      TDESC: description,
      USEREXP: description,
      PRIOR: '2',
      LOEKZ: null,
      TSTAT: '1',
      LODAT: null,
      ANDAT: null,
    };

    const result = await createTicket(newTicketPayload, { autoSelect: false });

    if (result && result.TICID) {
      // Yeni ticket'ı listeye ekle
      setTickets((prev) => [result, ...(prev || [])]);

      // Ticket'ı seç (cache/UI state hazırlığı)
      setSelectedTicketId(result.TICID);
      await selectTicket(result.TICID);

      // DetailView açılışını merkezi ve güvenli akıştan tetikle
      setSelectedPanel('flow');
      requestDetailView(result.TICID, 'new-ticket');
    }
  };

  const renderFlowItem = (flow, idx, showCategory = false) => {
    const isSelected = selectedFlow === flow.PROCESS;
    const label = flow.SRDESC || flow.PROCESS;
    console.log('flow', flow);
    return (
      <button
        type="button"
        key={`${flow.PROCESS}-${flow.MAINFLOW || ''}-${idx}`}
        onClick={() => {
          setSelectedFlow(flow.PROCESS);
          setSearchQuery('');
        }}
        title={flow.MAINFLOW || undefined}
        className={`group flex w-full items-center gap-2 px-2.5 py-1.5 text-left transition-colors ${
          idx !== 0 ? 'border-t border-white/5' : ''
        } ${isSelected ? 'bg-cyan-500/15' : 'hover:bg-white/5'}`}
      >
        <span className={`h-1.5 w-1.5 flex-shrink-0 rounded-full ${
          isSelected ? 'bg-cyan-400 shadow-[0_0_5px_rgba(34,211,238,0.8)]' : 'bg-white/20 group-hover:bg-white/40'
        }`} />
        <div className="flex min-w-0 flex-1 flex-col">
          <span className={`truncate text-[11.5px] font-medium leading-snug ${isSelected ? 'text-cyan-100' : 'text-slate-200'}`}>
            {label}
          </span>
          {showCategory && flow.FCATEGORY && (
            <span className="truncate text-[9.5px] text-slate-500">{flow.FCATEGORY}</span>
          )}
        </div>
        {isSelected && (
          <svg className="h-3 w-3 flex-shrink-0 text-cyan-400" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
            <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </button>
    );
  };

  return (
    <section className="flex h-full w-full flex-col overflow-y-auto bg-[#070b16] text-slate-100" style={{ scrollbarWidth: 'none' }}>
      <div className="relative flex w-full flex-col">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_20%_0%,rgba(92,124,255,0.2),transparent_50%)]" />
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_80%_0%,rgba(34,211,238,0.12),transparent_50%)]" />

        <div className="relative flex w-full flex-col">
          <div className="flex w-full flex-col">
            {/* Compact header */}
            <header className="flex items-center gap-3 border-b border-white/8 px-4 py-3">
              <div className="h-5 w-[3px] rounded-full bg-gradient-to-b from-cyan-400 to-blue-500" />
              <div>
                <p className="text-[10px] uppercase tracking-widest text-cyan-300/60">Yeni Kayıt</p>
                <h1 className="text-[13px] font-semibold text-white">Ticket Oluştur</h1>
              </div>
            </header>

            <form onSubmit={handleSubmit} className="flex flex-col gap-3 px-4 py-3">
              <input type="hidden" id="ticketFlow" value={selectedFlow} required />

              {/* Arama kutusu */}
              <div className="relative">
                <svg className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-500" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" strokeLinecap="round" />
                </svg>
                <input
                  ref={searchRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Süreç ara..."
                  className="w-full rounded-lg border border-white/10 bg-white/5 py-1.5 pl-8 pr-3 text-[12px] text-slate-100 outline-none placeholder:text-slate-500 focus:border-cyan-500/50 focus:bg-white/8"
                />
                {searchQuery && (
                  <button type="button" onClick={() => setSearchQuery('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300">
                    <svg className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                      <path d="M6 18L18 6M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                )}
              </div>

              {/* Arama sonuçları */}
              {searchResults !== null ? (
                <div className="rounded-lg border border-white/10 bg-[#0d1526]/80" style={{ maxHeight: 180, overflowY: 'auto', scrollbarWidth: 'thin', scrollbarColor: 'rgba(255,255,255,0.08) transparent' }}>
                  {searchResults.length === 0 ? (
                    <p className="px-3 py-3 text-[11px] text-slate-500">Sonuç bulunamadı.</p>
                  ) : (
                    searchResults.map((flow, idx) => renderFlowItem(flow, idx, true))
                  )}
                </div>
              ) : (
                /* Kategori accordion listesi */
                <div className="flex flex-col gap-1">
                  <p className="text-[10px] uppercase tracking-widest text-slate-500">Talep Türü</p>
                  <div className="rounded-lg border border-white/10 bg-[#0d1526]/60">
                    {categorizedFlows.map((group, gi) => {
                      const isOpen = activeCategory === group.category;
                      return (
                        <div key={group.category} className={gi !== 0 ? 'border-t border-white/8' : ''}>
                          {/* Kategori başlığı */}
                          <button
                            type="button"
                            onClick={() => setActiveCategory(isOpen ? '' : group.category)}
                            className={`flex w-full items-center gap-2 px-3 py-2 text-left transition-colors ${isOpen ? 'bg-white/5' : 'hover:bg-white/4'}`}
                          >
                            <svg
                              className={`h-3 w-3 flex-shrink-0 text-slate-500 transition-transform ${isOpen ? 'rotate-90' : ''}`}
                              fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"
                            >
                              <path d="M9 18l6-6-6-6" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                            <span className={`flex-1 truncate text-[12px] font-medium ${isOpen ? 'text-cyan-200' : 'text-slate-300'}`}>
                              {group.category}
                            </span>
                            <span className="flex-shrink-0 text-[10px] text-slate-600">{group.items.length}</span>
                          </button>

                          {/* Açık kategori flow listesi */}
                          {isOpen && (
                            <div className="border-t border-white/5 bg-black/20">
                              {group.items.map((flow, idx) => renderFlowItem(flow, idx, false))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Seçili süreç göstergesi */}
              {selectedFlow && (
                <div className="flex items-center gap-2 rounded-md border border-cyan-500/25 bg-cyan-500/10 px-2.5 py-1.5">
                  <svg className="h-3 w-3 flex-shrink-0 text-cyan-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="flex-1 truncate text-[11px] font-medium text-cyan-200">
                    {(flows || []).find((f) => f.PROCESS === selectedFlow)?.MAINFLOW || selectedFlow}
                  </span>
                  <button type="button" onClick={() => setSelectedFlow('')} className="flex-shrink-0 text-cyan-500/50 hover:text-cyan-300">
                    <svg className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                      <path d="M6 18L18 6M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                </div>
              )}

              {/* Ticket açıklaması */}
              <div className="flex flex-col gap-1.5">
                <label htmlFor="ticketDescription" className="text-[10px] uppercase tracking-widest text-slate-500">
                  Açıklama
                </label>
                <div className="relative rounded-lg border border-white/10 bg-white/5 focus-within:border-cyan-500/40">
                  <textarea
                    id="ticketDescription"
                    value={description}
                    onChange={handleDescriptionChange}
                    placeholder="Talebi 48 karakterde özetleyin..."
                    rows={3}
                    className="h-20 w-full resize-none rounded-lg bg-transparent px-3 py-2 text-[12px] text-slate-100 outline-none placeholder:text-slate-600"
                    style={{ textTransform: 'uppercase' }}
                  />
                  <span className={`absolute bottom-2 right-3 text-[10px] font-semibold ${remainingChars <= 6 ? 'text-orange-300' : 'text-slate-600'}`}>
                    {remainingChars}
                  </span>
                </div>
              </div>

              {/* Alt butonlar */}
              <div className="flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setSelectedPanel('flow')}
                  className="rounded-md border border-white/10 px-3 py-1.5 text-[11px] text-slate-400 transition hover:border-white/20 hover:text-slate-200"
                >
                  İptal
                </button>
                <button
                  type="submit"
                  disabled={!selectedFlow}
                  className="rounded-md bg-gradient-to-r from-cyan-600 to-blue-600 px-4 py-1.5 text-[11px] font-semibold text-white shadow-md transition hover:from-cyan-500 hover:to-blue-500 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Kaydı Oluştur
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

export default NewTicket;
